import { useCallback, useEffect, useState } from "react";
import { createPortal } from "react-dom";
import {
  fetchEvent,
  fetchEventHistory,
  fetchEventImpact,
  updateEvent,
  deleteEvent,
  describeEvent,
  removePhotosFromEvent,
  thumbUrl,
  type Comment,
  type EventEdit,
  type EventField,
  type EventImpact,
  type EventSummary,
  type Photo,
} from "./api.js";
import { useAuth } from "./auth.js";
import { useRecording } from "./recording.js";
import { navigate, goBack } from "./router.js";
import { HistoryIcon, TrashIcon } from "./icons.js";
import { InlineField, ellipsis, fmtWhen, isDraft } from "./shared.js";
import { AlbumComments } from "./comments.js";
import { PhotoViewer } from "./photo-viewer.js";

/**
 * L'écran d'un album : son en-tête éditable, sa description, son fil de commentaires et sa
 * grille de photos.
 */
/**
 * Ce que la suppression emporte, en une phrase. Le point important : distinguer les
 * photos de l'album de celles qui retomberaient vraiment dans « À trier » — depuis le
 * multi-album, ce sont deux nombres différents, et souvent le second vaut zéro.
 */
function impactPhrase(i: EventImpact): string {
  const bouts: string[] = [];
  if (i.becomesLoose > 0) {
    bouts.push(
      `${i.becomesLoose} photo${i.becomesLoose > 1 ? "s" : ""} retourne${i.becomesLoose > 1 ? "nt" : ""} dans « À trier »`,
    );
  }
  const ailleurs = i.photoCount - i.becomesLoose;
  if (ailleurs > 0) {
    bouts.push(`${ailleurs} reste${ailleurs > 1 ? "nt" : ""} dans d'autres albums`);
  }
  if (i.commentCount > 0) {
    bouts.push(
      `${i.commentCount} commentaire${i.commentCount > 1 ? "s" : ""} ${i.commentCount > 1 ? "seront perdus" : "sera perdu"}`,
    );
  }
  return bouts.length > 0 ? `${bouts.join(", ")}.` : "Aucune photo, aucun commentaire.";
}

function EventHeader({
  event,
  photoCount,
  onEvent,
  onDeleted,
}: {
  event: EventSummary;
  photoCount: number;
  onEvent: (ev: EventSummary) => void;
  onDeleted: () => void;
}) {
  const { state } = useAuth();
  /** Ranger donne accès à l'historique ; seul le propriétaire peut supprimer l'album. */
  const peutRanger = state.status === "authed" && state.isEditor;
  const isOwner = state.status === "authed" && state.isOwner;
  const [historyOpen, setHistoryOpen] = useState(false);
  // null = pas de demande en cours ; l'impact est chargé avant d'afficher la confirmation,
  // pour ne jamais demander « tu es sûr ? » sans dire de quoi.
  const [impact, setImpact] = useState<EventImpact | null>(null);
  const [deleting, setDeleting] = useState(false);

  const remove = async () => {
    setDeleting(true);
    try {
      await deleteEvent(event.id);
      onDeleted();
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div className="evhead">
      <div className="evhead__row">
        <h2 className="gallery__title">
          <InlineField
            value={event.title}
            placeholder="Titre de l'album"
            className="editable evhead__title"
            inputClassName="evhead__title-in"
            onSave={async (v) => onEvent(await updateEvent(event.id, { title: v }))}
            testId="ev-title"
          />
          <InlineField
            value={event.dateApprox ?? ""}
            placeholder="＋ date"
            className="editable evhead__date"
            inputClassName="evhead__date-in"
            onSave={async (v) => onEvent(await updateEvent(event.id, { dateApprox: v }))}
            testId="ev-date"
          />
        </h2>
        {/* Consulter l'historique accompagne le rangement ; supprimer l'album non. */}
        {peutRanger && (
          <button
            className="evhead__hist"
            onClick={() => setHistoryOpen(true)}
            aria-label="Historique des modifications"
            title="Historique des modifications"
            data-testid="ev-history"
          >
            <HistoryIcon />
          </button>
        )}
        {isOwner && (
          <button
            className="evhead__del"
            onClick={() => void fetchEventImpact(event.id).then(setImpact)}
            aria-label="Supprimer l'album"
            title="Supprimer l'album"
            data-testid="ev-delete"
          >
            <TrashIcon />
          </button>
        )}
      </div>
      <p className="evhead__count" data-testid="ev-count">
        {photoCount} photo{photoCount > 1 ? "s" : ""}
      </p>
      {impact && (
        <p className="delbar" data-testid="delbar">
          <span className="delbar__what">
            Supprimer « {event.title || "Sans titre"} » ? Les photos ne sont pas
            supprimées&nbsp;: {impactPhrase(impact)}
          </span>
          <button className="cbox__yes" disabled={deleting} onClick={() => void remove()}>
            {deleting ? "Suppression…" : "Supprimer"}
          </button>
          <button className="cbox__no" onClick={() => setImpact(null)}>Annuler</button>
        </p>
      )}
      {historyOpen && <HistoryModal event={event} onClose={() => setHistoryOpen(false)} onEvent={onEvent} />}
    </div>
  );
}

const FIELD_LABELS: Record<EventField, string> = {
  title: "Titre",
  dateApprox: "Date",
  description: "Description",
};

function HistoryModal({
  event,
  onClose,
  onEvent,
}: {
  event: EventSummary;
  onClose: () => void;
  onEvent: (ev: EventSummary) => void;
}) {
  const [edits, setEdits] = useState<EventEdit[] | null>(null);
  const load = useCallback(() => {
    fetchEventHistory(event.id).then(setEdits).catch(() => setEdits([]));
  }, [event.id]);
  useEffect(() => load(), [load]);

  const restore = async (field: EventField, value: string | null) => {
    onEvent(await updateEvent(event.id, { [field]: value ?? "" }));
    load();
  };

  return createPortal(
    <div className="modal" data-testid="history-modal" onClick={onClose}>
      <div className="modal__card" onClick={(e) => e.stopPropagation()}>
        <header className="modal__head">
          <h3 className="modal__title">Historique des modifications</h3>
          <button className="modal__close" onClick={onClose} aria-label="Fermer">×</button>
        </header>
        {!edits ? (
          <p className="modal__muted">Chargement…</p>
        ) : edits.length === 0 ? (
          <p className="modal__muted">Aucune modification enregistrée.</p>
        ) : (
          <ul className="hist">
            {edits.map((e) => (
              <li key={e.id} className="hist__row" data-testid="hist-row">
                <div className="hist__info">
                  <span className="hist__change">
                    <span className="hist__field">{FIELD_LABELS[e.field] ?? e.field}</span>
                    {" « "}
                    {ellipsis(e.from)}
                    {" » → « "}
                    {ellipsis(e.to)}
                    {" »"}
                  </span>
                  <span className="hist__meta">{e.authorName} · {fmtWhen(e.at)}</span>
                </div>
                {e.from !== null && e.from !== undefined && (
                  <button
                    className="hist__restore"
                    onClick={() => void restore(e.field, e.from)}
                    data-testid="hist-restore"
                  >
                    Restaurer
                  </button>
                )}
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>,
    document.body,
  );
}

export function EventView({
  id,
  photoId,
  onBack,
}: {
  id: string;
  photoId: string | null;
  onBack: () => void;
}) {
  const [data, setData] = useState<{
    event: EventSummary;
    photos: Photo[];
    comments: Comment[];
  } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const { dataVersion, refreshData, status } = useRecording();
  const { state: authState } = useAuth();
  const peutRanger = authState.status === "authed" && authState.isEditor;

  useEffect(() => {
    let cancelled = false;
    fetchEvent(id)
      .then((d) => {
        if (!cancelled) setData(d);
      })
      .catch((e: unknown) => {
        if (!cancelled) setError(e instanceof Error ? e.message : String(e));
      });
    return () => {
      cancelled = true;
    };
    // Refetch quand les données changent (transcription, suppression).
  }, [id, dataVersion]);

  if (error) return <p className="gallery__error">Erreur : {error}</p>;
  if (!data) return <div className="gallery__loading">Chargement…</div>;

  const { event, photos, comments } = data;
  // L'URL est la source de vérité. Un lien vers une photo qui n'est plus dans cet
  // album (elle a été déplacée) ouvre simplement l'album.
  const viewer = photoId ? photos.findIndex((p) => p.id === photoId) : -1;
  const openPhoto = (i: number) =>
    navigate({ kind: "album", id, photoId: photos[i]!.id });

  return (
    <div className="gallery" data-testid="event-view">
      <button className="back" onClick={onBack}>‹ Albums</button>
      <EventHeader
        event={event}
        photoCount={photos.length}
        onEvent={(ev) => setData((d) => (d ? { ...d, event: ev } : d))}
        onDeleted={onBack}
      />

      {peutRanger && isDraft(event) && (
        <DissolveDraft eventId={event.id} photoIds={photos.map((p) => p.id)} onDissolved={onBack} />
      )}

      <Description
        event={event}
        hasComments={comments.length > 0}
        onEvent={(ev) => setData((d) => (d ? { ...d, event: ev } : d))}
      />

      <AlbumComments
        eventId={event.id}
        comments={comments}
        busy={status === "transcribing"}
        onOpenPhoto={(pid) => navigate({ kind: "album", id, photoId: pid })}
        onChanged={refreshData}
      />

      <div className="grid grid--photos">
        {photos.map((p, i) => (
          <button
            key={p.id}
            className="tile"
            onClick={() => openPhoto(i)}
            data-testid="photo-tile"
          >
            <img src={thumbUrl(p.id)} alt={p.flickrDescription || p.scanName} loading="lazy" />
          </button>
        ))}
      </div>
      {viewer !== -1 && (
        <PhotoViewer
          photos={photos}
          index={viewer}
          eventId={event.id}
          albumTitle={event.title || "Sans titre"}
          onIndex={(i) =>
            // Remplacer et non empiler : « retour » doit ramener à l'album, pas
            // rejouer une à une les photos parcourues aux flèches.
            navigate({ kind: "album", id, photoId: photos[i]!.id }, { replace: true })
          }
          onClose={() => goBack({ kind: "album", id, photoId: null })}
        />
      )}
    </div>
  );
}

/** Défaire un regroupement raté : les photos repartent dans « À trier ». */
function DissolveDraft({
  eventId,
  photoIds,
  onDissolved,
}: {
  eventId: string;
  photoIds: string[];
  onDissolved: () => void;
}) {
  const [confirming, setConfirming] = useState(false);
  const [busy, setBusy] = useState(false);

  const dissolve = async () => {
    setBusy(true);
    try {
      await removePhotosFromEvent(eventId, photoIds);
      onDissolved();
    } finally {
      setBusy(false);
    }
  };

  return (
    <p className="draftbar" data-testid="draftbar">
      <span className="draftbar__tag">Brouillon</span>
      <span className="draftbar__hint">
        Clique le titre ci-dessus pour le nommer, ou laisse la famille le raconter en session.
      </span>
      {confirming ? (
        <span className="cbox__confirm">
          Renvoyer les {photoIds.length} photos dans « À trier » ?
          <button className="cbox__yes" disabled={busy} onClick={() => void dissolve()}>Oui</button>
          <button className="cbox__no" onClick={() => setConfirming(false)}>Non</button>
        </span>
      ) : (
        <button className="draftbar__dissolve" onClick={() => setConfirming(true)} data-testid="dissolve">
          Dissoudre
        </button>
      )}
    </p>
  );
}

/**
 * Description de l'album : texte libre, collaboratif et historisé. Diapo peut la
 * rédiger à partir des commentaires ; une description écrite à la main est
 * souveraine (l'assistant ne la remplace jamais).
 */
function Description({
  event,
  hasComments,
  onEvent,
}: {
  event: EventSummary;
  hasComments: boolean;
  onEvent: (ev: EventSummary) => void;
}) {
  const { state } = useAuth();
  const peutRanger = state.status === "authed" && state.isEditor;
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState("");
  const [saving, setSaving] = useState(false);
  const [describing, setDescribing] = useState(false);
  const text = event.description?.trim() ?? "";
  const byDiapo = event.descriptionBy === "Diapo";

  const startEdit = () => {
    setDraft(text);
    setEditing(true);
  };
  const save = async () => {
    setSaving(true);
    try {
      onEvent(await updateEvent(event.id, { description: draft }));
      setEditing(false);
    } finally {
      setSaving(false);
    }
  };
  const describe = async () => {
    setDescribing(true);
    try {
      onEvent(await describeEvent(event.id));
    } finally {
      setDescribing(false);
    }
  };

  if (editing) {
    return (
      <section className="evdesc" data-testid="description">
        <textarea
          className="evdesc__input"
          value={draft}
          autoFocus
          rows={4}
          placeholder="L'histoire de cet album…"
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Escape") setEditing(false);
            // Entrée fait un retour à la ligne ici : ⌘/Ctrl+Entrée valide.
            if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) void save();
          }}
          data-testid="desc-input"
        />
        <div className="evdesc__actions">
          <button className="evdesc__save" onClick={() => void save()} disabled={saving} data-testid="desc-save">
            Enregistrer
          </button>
          <button className="evdesc__cancel" onClick={() => setEditing(false)}>Annuler</button>
        </div>
      </section>
    );
  }

  return (
    <section className="evdesc" data-testid="description">
      {text ? (
        <>
          <div
            className="evdesc__text editable"
            role="button"
            tabIndex={0}
            title="Cliquer pour modifier"
            onClick={startEdit}
            onKeyDown={(e) => {
              if (e.key === "Enter") startEdit();
            }}
            data-testid="desc-edit"
          >
            {text}
          </div>
          {peutRanger && hasComments && byDiapo && (
            <div className="evdesc__foot">
              <button
                className="evdesc__diapo"
                onClick={() => void describe()}
                disabled={describing}
                data-testid="desc-diapo"
              >
                {describing ? "Diapo rédige…" : "Réécrire"}
              </button>
            </div>
          )}
        </>
      ) : (
        <div className="evdesc__empty">
          <button className="evdesc__add" onClick={startEdit} data-testid="desc-add">
            ＋ Ajouter une description
          </button>
          {peutRanger && hasComments && (
            <button
              className="evdesc__diapo"
              onClick={() => void describe()}
              disabled={describing}
              data-testid="desc-diapo"
            >
              {describing ? "Diapo rédige…" : "Rédiger avec Diapo"}
            </button>
          )}
        </div>
      )}
    </section>
  );
}
